import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useFeatures } from './contexts/FeaturesContext';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/admin/AdminLayout';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import LeagueList from './pages/LeagueList';
import LeagueDetail from './pages/LeagueDetail';
import MatchDetail from './pages/MatchDetail';

// Pair & Team pages
import PairManagement from './pages/PairManagement';
import TeamManagement from './pages/TeamManagement';
import TeamDetail from './pages/TeamDetail';
import UserPublicProfile from './pages/UserPublicProfile';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Season & ELO pages
import Ranking from './pages/Ranking';
import Courts from './pages/Courts';
import SeasonList from './pages/SeasonList';
import SeasonDetail from './pages/SeasonDetail';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminLeagueNew from './pages/admin/AdminLeagueNew';
import AdminLeagueManage from './pages/admin/AdminLeagueManage';
import AdminLeagueDraw from './pages/admin/AdminLeagueDraw';
import AdminUsers from './pages/admin/AdminUsers';
import AdminSeasonNew from './pages/admin/AdminSeasonNew';
import AdminSeasonManage from './pages/admin/AdminSeasonManage';
import AdminAuditLog from './pages/admin/AdminAuditLog';
import Impressum from './pages/Impressum';
import AGB from './pages/AGB';
import Datenschutz from './pages/Datenschutz';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
      <CookieBanner />
    </div>
  );
}

function AppRoutes() {
  const { features } = useFeatures();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/courts" element={<Courts />} />
      <Route path="/users/:id" element={<UserPublicProfile />} />

      {/* Liga feature */}
      <Route path="/leagues" element={features.liga ? <LeagueList /> : <Navigate to="/" replace />} />
      <Route path="/leagues/:id" element={features.liga ? <LeagueDetail /> : <Navigate to="/" replace />} />
      <Route path="/matches/:id" element={features.liga ? <MatchDetail /> : <Navigate to="/" replace />} />
      {/* Pairs are used both by Paar-Liga (liga) and pairs-type Saisons */}
      <Route path="/pairs" element={features.liga || features.saison
        ? <ProtectedRoute><PairManagement /></ProtectedRoute>
        : <Navigate to="/" replace />}
      />

      {/* Saison feature */}
      <Route path="/seasons" element={features.saison ? <SeasonList /> : <Navigate to="/" replace />} />
      <Route path="/seasons/:id" element={features.saison ? <SeasonDetail /> : <Navigate to="/" replace />} />
      <Route path="/ranking" element={features.saison ? <Ranking /> : <Navigate to="/" replace />} />

      {/* Teams are used both by Mannschafts-Liga (mannschaft) and wettkampf-type Saisons */}
      <Route path="/teams" element={features.mannschaft || features.saison
        ? <ProtectedRoute><TeamManagement /></ProtectedRoute>
        : <Navigate to="/" replace />}
      />
      <Route path="/teams/:id" element={features.mannschaft ? <TeamDetail /> : <Navigate to="/" replace />} />

      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/profile/edit" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />

      {/* Admin routes — always accessible for admin */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/leagues/new" element={<ProtectedRoute adminOnly><AdminLayout><AdminLeagueNew /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/leagues/:id" element={<ProtectedRoute adminOnly><AdminLayout><AdminLeagueManage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/leagues/:id/draw" element={<ProtectedRoute adminOnly><AdminLayout><AdminLeagueDraw /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminLayout><AdminUsers /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/seasons/new" element={<ProtectedRoute adminOnly><AdminLayout><AdminSeasonNew /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/seasons/:id" element={<ProtectedRoute adminOnly><AdminLayout><AdminSeasonManage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/audit-log" element={<ProtectedRoute adminOnly><AdminLayout><AdminAuditLog /></AdminLayout></ProtectedRoute>} />

      {/* Legal */}
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/agb" element={<AGB />} />
      <Route path="/datenschutz" element={<Datenschutz />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Layout>
          <AppRoutes />
        </Layout>
      </AuthProvider>
    </BrowserRouter>
  );
}
